import { useCallback } from "react"
import { useTipoConsumoApi } from "../../infra/tipo-consumo"
import { useAppDispatch, useAppSelector } from "../../presentation/hooks/store-hooks"
import { setProdutosTipoConsumoState } from "../../application/states/produto"
import { useUIService } from "../ui"
import {
    setVincularTipoConsumoCreateClienteFormFieldsState,
    removeTipoConsumoCreateClienteFormFieldsState,
    setVincularProdutoCreateClienteFormFieldsState
} from "../../application/states/clientes"

export const useTipoConsumoService = () => {
    // dispatch
    const dispatch = useAppDispatch()
    // State
    const { currentUser } = useAppSelector((state) => state.user)
    const { produtosTipoConsumo } = useAppSelector((state) => state.produto)
    const { createClienteForm } = useAppSelector((state) => state.clientes)
    // Infra
    const { 
        getAllProdutosAndTipoConsumosApi,
        getProdutoTipoSConsumoPadraoApi
    } = useTipoConsumoApi()
    // Service
    const { setAlert, setLoading } = useUIService()

    const setAllProdutosAndTipoConsumo = useCallback(async (idCliente?: number) => {
        const loadingId = Math.random().toString()
        if (currentUser) {
            try {
                setLoading({
                    id: loadingId,
                    loading: true,
                    message: 'Buscando produtos...'
                })

                const getAllProdutosAndTipoConsumo = await getAllProdutosAndTipoConsumosApi({
                    IdCliente: typeof idCliente !== 'undefined' ? idCliente : currentUser.IdCliente
                })


                dispatch(setProdutosTipoConsumoState(getAllProdutosAndTipoConsumo.produtoClienteTipoConsumo))
            } catch (error: any) {
                setAlert({
                    message: error.response?.data?.error || 'Erro ao buscar produtos e tipos de consumo',
                    type: 'error'
                })
            } finally {
                setLoading({
                    id: loadingId,
                    loading: false
                })
            }
        }
    }, [currentUser, dispatch, getAllProdutosAndTipoConsumosApi, setAlert, setLoading])

    const setProdutosAndTipoConsumoByIdCliente = useCallback(async (idCliente: number) => {
        const loadingId = Math.random().toString()
        try {
            setLoading({
                id: loadingId,
                loading: true,
                message: 'Buscando produtos do cliente...'
            })

            const produtosTipoConsumo = await getAllProdutosAndTipoConsumosApi({
                IdCliente: idCliente
            })

            const produtosUnicos = new Set<string>();
            const tiposConsumoUnicos = new Set<string>();

            produtosTipoConsumo.produtoClienteTipoConsumo.forEach(produtoTipoConsumo => {
                produtosUnicos.add(produtoTipoConsumo.produto.Descricao);

                produtoTipoConsumo.tipoConsumo.forEach(tipoConsumo => {
                    tiposConsumoUnicos.add(tipoConsumo.Descricao);
                });
            });

            const listaProdutos = Array.from(produtosUnicos);
            const listaTiposConsumo = Array.from(tiposConsumoUnicos);

            dispatch(setProdutosTipoConsumoState(produtosTipoConsumo.produtoClienteTipoConsumo));

            return {
                produtos: listaProdutos,
                tiposConsumo: listaTiposConsumo,
                dadosCompletos: produtosTipoConsumo
            };
        } catch (error: any) {
            setAlert({
                message: error.response?.data?.error || 'Erro ao buscar produtos do cliente',
                type: 'error'
            });
            return {
                produtos: [],
                tiposConsumo: [],
                dadosCompletos: []
            };
        } finally {
            setLoading({
                id: loadingId,
                loading: false
            });
        }
    }, [dispatch, getAllProdutosAndTipoConsumosApi, setAlert, setLoading]);

    const vincularTipoConsumoCreateClienteForm = useCallback((data: { idProduto: number; idTipoConsumo: number }) => {
        if (createClienteForm.fields.ProdutosTiposConsumos.find((tipoConsumo) => tipoConsumo.IdProduto === data.idProduto)) {
            dispatch(setVincularTipoConsumoCreateClienteFormFieldsState({
                idProduto: data.idProduto,
                idTipoConsumo: data.idTipoConsumo
            }))
        } else {
            dispatch(setVincularProdutoCreateClienteFormFieldsState({
                idProduto: data.idProduto
            }))
            dispatch(setVincularTipoConsumoCreateClienteFormFieldsState({
                idProduto: data.idProduto,
                idTipoConsumo: data.idTipoConsumo
            }))
        }
    }, [dispatch, createClienteForm.fields.ProdutosTiposConsumos])

    const removerTipoConsumoCreateClienteForm = useCallback((data: { idProduto: number; idTipoConsumo: number }) => {
        dispatch(removeTipoConsumoCreateClienteFormFieldsState({
            idProduto: data.idProduto,
            idTipoConsumo: data.idTipoConsumo
        }))
    }, [dispatch])

    const validateTiposConsumoPorProduto = useCallback((): { isValid: boolean; errorMessage?: string } => {
        if (!createClienteForm.fields.ProdutosTiposConsumos || createClienteForm.fields.ProdutosTiposConsumos.length === 0) {
            return { isValid: true }
        }

        const produtosSemTipoConsumo = createClienteForm.fields.ProdutosTiposConsumos.filter(
            produto => !produto.IdTipoConsumo || produto.IdTipoConsumo.length === 0
        )

        if (produtosSemTipoConsumo.length > 0) {
            const produtosNomes = produtosSemTipoConsumo.map(produto => {
                const produtoInfo = produtosTipoConsumo.find(p => p.produto.IdProduto === produto.IdProduto)
                return produtoInfo?.produto.Descricao || `Produto ${produto.IdProduto}`
            })

            return {
                isValid: false,
                errorMessage: `Os seguintes produtos precisam ter pelo menos um tipo de consumo selecionado: ${produtosNomes.join(', ')}`
            }
        }

        return { isValid: true }
    }, [createClienteForm.fields.ProdutosTiposConsumos, produtosTipoConsumo])

    const isTipoConsumoSelecionado = useCallback((idProduto: number, idTipoConsumo: number): boolean => {
        const produto = createClienteForm.fields.ProdutosTiposConsumos?.find(
            p => p.IdProduto === idProduto
        )
        return produto?.IdTipoConsumo?.includes(idTipoConsumo) || false
    }, [createClienteForm.fields.ProdutosTiposConsumos])

    const getProdutoTipoSConsumoPadrao = useCallback(async (): Promise<any[]> => {
        try {
            const result = await getProdutoTipoSConsumoPadraoApi()
            return result
        }
        catch (error) {
            throw error
        }
    }, [getProdutoTipoSConsumoPadraoApi])

    return {
        setAllProdutosAndTipoConsumo,
        setProdutosAndTipoConsumoByIdCliente,
        vincularTipoConsumoCreateClienteForm,
        removerTipoConsumoCreateClienteForm,
        validateTiposConsumoPorProduto,
        isTipoConsumoSelecionado,
        getProdutoTipoSConsumoPadrao
    }
}
